import { snackBarService } from './../../../../services/snack-bar.service';
import { HttpClient } from '@angular/common/http';
import { Location } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { AngularMaterialModule } from '../../../../angular-material.module';
import { CommonModule } from '@angular/common';
import { environment } from '../../../../../environments/environment';
import { RaiseRequestService } from '../../../../services/raise-request.service';
import { AssignService } from '../../../../Modules/assign/assign.service';
import { getAccountGroupIdFromSession } from '../../../../Modules/assets/asset-movement/asset-movement.utils';

@Component({
  selector: 'app-raise-requests',
  standalone: true,
  imports: [FormsModule, ReactiveFormsModule, AngularMaterialModule, CommonModule],
  templateUrl: './raise-requests.component.html',
  styleUrls: ['./raise-requests.component.scss'],
})
export class RaiseRequestsComponent implements OnInit {
  categoryList: any[] = [];
  subCategoryList: any[] = [];
  showSubCategory = false;
  projectList: any[] = [];
  statusList: any[] = [];
  priorityList: any[] = [];
  roleList: any[] = [];
  allMembers: any[] = [];
  baseUrl = environment.baseUrl;
  issuePhotoPreview: string | null = null;
  issueVideoPreview: string | null = null;
  uploadedFileName: string | null = null;
  isVideoUpload = false;
  submitting = false;
  private dialogRef = inject(MatDialogRef<RaiseRequestsComponent>, { optional: true });

  raiseReuqestForm = new FormGroup({
    account_id: new FormControl(sessionStorage.getItem('account_id') || '', [Validators.required]),
    user_id: new FormControl(sessionStorage.getItem('session_id')),
    issue_category_id: new FormControl('', [Validators.required]),
    sub_issue_category_id: new FormControl(''),
    description: new FormControl('', [Validators.required]),
    status: new FormControl<number | string>(''),
    priority_id: new FormControl<number | string>(''),
    role_id: new FormControl<number | string>(''),
    assigned_to: new FormControl<number | string>(''),
    issue_photo: new FormControl<File | null>(null, [Validators.required]),
  });

  constructor(
    private raiseRequestService: RaiseRequestService,
    private snackBarService: snackBarService,
    private http: HttpClient,
    private assignService: AssignService,
    private location: Location,
  ) {}

  ngOnInit(): void {
    this.fetchProjectDropdown();
    this.loadCategoryDropdown();
    this.fetchAllStatus();
    this.fetchAllPriority();
    this.fetchAllRoles();

    this.raiseReuqestForm.get('issue_category_id')?.valueChanges.subscribe((categoryId) => {
      this.onCategoryChange(categoryId);
    });

    this.raiseReuqestForm.get('role_id')?.valueChanges.subscribe((roleId) => {
      this.raiseReuqestForm.get('assigned_to')?.reset('');
      if (roleId) {
        this.fetchStaffByRole(roleId);
      } else {
        this.allMembers = [];
      }
    });
  }

  onCategoryChange(categoryId: string | number | null | undefined): void {
    this.raiseReuqestForm.get('sub_issue_category_id')?.reset('');
    this.subCategoryList = [];
    this.showSubCategory = false;
    this.updateSubCategoryValidation();

    if (!categoryId) {
      return;
    }

    this.raiseRequestService.getSubCategories({ issue_category_id: categoryId }).subscribe({
      next: (res: any) => {
        const list = Array.isArray(res) ? res : (res?.data ?? []);
        this.subCategoryList = list;
        this.showSubCategory = list.length > 0;
        this.updateSubCategoryValidation();
      },
      error: (err: any) => {
        console.error('Error fetching subcategories:', err);
        this.subCategoryList = [];
        this.showSubCategory = false;
        this.updateSubCategoryValidation();
      },
    });
  }

  private updateSubCategoryValidation(): void {
    const control = this.raiseReuqestForm.get('sub_issue_category_id');
    if (!control) {
      return;
    }

    if (this.showSubCategory) {
      control.setValidators([Validators.required]);
    } else {
      control.clearValidators();
    }
    control.updateValueAndValidity({ emitEvent: false });
  }

  loadCategoryDropdown(): void {
    const accountGroupId = getAccountGroupIdFromSession();
    if (accountGroupId == null) {
      this.categoryList = [];
      return;
    }

    this.raiseRequestService.getCategories({ account_group_id: accountGroupId }).subscribe({
      next: (res: any) => {
        this.categoryList = Array.isArray(res) ? res : [];
      },
      error: (err: any) => {
        console.error('Error fetching categories:', err);
        this.snackBarService.showError('Failed to fetch categories, please try again later.');
        this.categoryList = [];
      },
    });
  }

  fetchProjectDropdown(): void {
    const userId = sessionStorage.getItem('session_id');
    if (!userId) {
      this.projectList = [];
      return;
    }

    const parsedUserId = parseInt(userId, 10);
    if (Number.isNaN(parsedUserId)) {
      this.projectList = [];
      return;
    }

    this.assignService.fetchAssignedProjectsDropdown(parsedUserId).subscribe({
      next: (res: any) => {
        if (res?.success && res?.data) {
          this.projectList = res.data;
        } else if (Array.isArray(res)) {
          this.projectList = res;
        } else {
          this.projectList = res?.data || [];
        }
      },
      error: (err: any) => {
        console.error('Error fetching projects:', err);
        this.snackBarService.showError('Failed to fetch projects, please try again later.');
      },
    });
  }

  fetchAllStatus(): void {
    this.http.get(`${this.baseUrl}/status_dropdown`).subscribe({
      next: (res: any) => {
        this.statusList = Array.isArray(res) ? res : [];
      },
      error: (err: any) => {
        console.error('Error fetching statuses:', err);
        this.snackBarService.showError('Failed to fetch statuses.');
      },
    });
  }

  fetchAllPriority(): void {
    this.http.get(`${this.baseUrl}/priority_dropdown`).subscribe({
      next: (res: any) => {
        this.priorityList = Array.isArray(res) ? res : [];
      },
      error: (err: any) => {
        console.error('Error fetching priorities:', err);
        this.snackBarService.showError('Failed to fetch internal priorities.');
      },
    });
  }

  fetchAllRoles(accountId?: string | number | null): void {
    const accountIdToUse =
      accountId || this.raiseReuqestForm.get('account_id')?.value || sessionStorage.getItem('account_id');
    if (!accountIdToUse) {
      this.roleList = [];
      return;
    }

    this.http.get(`${this.baseUrl}/role_dropdown`).subscribe({
      next: (res: any) => {
        this.roleList = Array.isArray(res) ? res : [];
      },
      error: (err: any) => {
        console.error('Error fetching roles:', err);
        this.snackBarService.showError('Failed to fetch roles.');
        this.roleList = [];
      },
    });
  }

  fetchStaffByRole(roleId: string | number): void {
    const accountId =
      this.raiseReuqestForm.get('account_id')?.value || sessionStorage.getItem('account_id');
    if (!roleId || !accountId) {
      this.allMembers = [];
      return;
    }

    const payload = { account_id: accountId, role_id: roleId };
    this.http.post(`${this.baseUrl}/staff_dropdown`, payload).subscribe({
      next: (res: any) => {
        const staff = Array.isArray(res) ? res : (res?.data ?? []);
        this.allMembers = staff.map((m: any) => ({
          ...m,
          member_name: `${m.first_name ?? ''} ${m.last_name ?? ''}`.trim(),
        }));
      },
      error: (err: any) => {
        console.error('Error fetching members:', err);
        this.snackBarService.showError('Failed to fetch members.');
        this.allMembers = [];
      },
    });
  }

  onProjectChange(accountId: string | number | null): void {
    this.raiseReuqestForm.get('role_id')?.reset('');
    this.raiseReuqestForm.get('assigned_to')?.reset('');
    this.allMembers = [];
    this.fetchAllRoles(accountId);
  }

  onFileSelect(event: Event): void {
    const fileInput = event.target as HTMLInputElement;
    const file = fileInput.files?.[0];
    if (!file) {
      return;
    }

    this.isVideoUpload = file.type.startsWith('video/');
    this.uploadedFileName = file.name;
    this.raiseReuqestForm.patchValue({ issue_photo: file });

    if (this.isVideoUpload) {
      this.issuePhotoPreview = null;
      this.issueVideoPreview = URL.createObjectURL(file);
    } else {
      this.issueVideoPreview = null;
      const reader = new FileReader();
      reader.onload = () => {
        this.issuePhotoPreview = reader.result as string;
      };
      reader.readAsDataURL(file);
    }
  }

  deleteFile(): void {
    this.raiseReuqestForm.get('issue_photo')?.setValue(null);
    this.raiseReuqestForm.get('issue_photo')?.markAsTouched();
    this.issuePhotoPreview = null;
    if (this.issueVideoPreview) {
      URL.revokeObjectURL(this.issueVideoPreview);
    }
    this.issueVideoPreview = null;
    this.uploadedFileName = null;
    this.isVideoUpload = false;
  }

  submitRequest(): void {
    this.raiseReuqestForm.markAllAsTouched();

    if (this.raiseReuqestForm.invalid) {
      this.snackBarService.showError('Please fill in all required fields');
      return;
    }

    this.submitting = true;
    const formData = new FormData();
    const rawValue = this.raiseReuqestForm.getRawValue();

    const finalCategoryId =
      rawValue.sub_issue_category_id || rawValue.issue_category_id;

    Object.entries(rawValue).forEach(([key, value]) => {
      if (value === null || value === undefined || value === '') {
        return;
      }
      if (key === 'sub_issue_category_id' || key === 'issue_category_id') {
        return;
      }
      if (value instanceof File) {
        formData.append(key, value);
      } else {
        formData.append(key, String(value));
      }
    });

    if (finalCategoryId) {
      formData.append('issue_category_id', String(finalCategoryId));
    }

    if (!formData.has('status')) {
      formData.append('status', '1');
    }

    const floorUnitId = sessionStorage.getItem('floor_unit_id');
    if (floorUnitId && floorUnitId.trim() !== '' && floorUnitId !== 'null' && floorUnitId !== 'undefined') {
      formData.append('floor_unit_id', floorUnitId);
    }

    this.raiseRequestService.submitIssue(formData).subscribe({
      next: (res: any) => {
        if (res.success) {
          this.closeView(true);
          this.snackBarService.showSuccess('Report Issue Submitted');
        } else {
          this.snackBarService.showError('Failed to submit request');
        }
      },
      error: (err: any) => {
        console.error('Error occurred:', err);
        this.snackBarService.showError('Error submitting request');
      },
      complete: () => {
        this.submitting = false;
        this.raiseReuqestForm.reset();
        this.deleteFile();
      },
    });
  }

  closeView(result = false): void {
    if (this.dialogRef) {
      this.dialogRef.close(result);
      return;
    }
    this.location.back();
  }
}
